import { traducirError, useTraduccion } from '@tallerya/i18n';
import { AvisoError, Boton, useAvisos, Ventana } from '@tallerya/ui';
import { useState } from 'react';
import { nombreDe, useCambiosPersona, type Persona } from './datos';

// Confirma la baja de una persona (#58), con el molde de `EliminarActivo`. La baja
// es lógica, y el código no queda libre: su usuario sigue existiendo en Keycloak.
//
// **Con trabajo registrado no se elimina; se desactiva** (#163): aparece en una
// orden, o firmó algún cambio. Entonces la ventana cambia a «No se puede
// eliminar», dice por qué y, **solo si sigue activa**, ofrece desactivarla. El
// texto de la API (`error.staff.tiene_historial`) dice «Puedes desactivarla»
// siempre, y con alguien ya inactivo no sirve: por eso se arma aquí.

interface Props {
  /** Lo que dice el texto. Sirve la fila del listado y la ficha. */
  persona: Pick<Persona, 'id' | 'firstName' | 'lastName' | 'employeeCode'> | null;
  onCerrar: () => void;
  /** Después de eliminarla. La ficha y la edición vuelven al listado. */
  onEliminado?: () => void;
}

/** Por qué no se pudo: tiene historial, u otro problema ya traducido. */
type Rechazo = { tipo: 'historial'; ordenes: number; activa: boolean } | { tipo: 'otro'; texto: string };

export function EliminarPersona({ persona, onCerrar, onEliminado }: Props) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosPersona();
  const [ocupado, setOcupado] = useState(false);
  const [rechazo, setRechazo] = useState<Rechazo | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);

  const nombre = persona ? nombreDe(persona) : '';
  const sinRespuesta = t('ui.formulario.aviso.sinRespuesta');

  function cerrar() {
    if (ocupado) return;
    setRechazo(null);
    setFallo(null);
    onCerrar();
  }

  async function eliminar() {
    if (!persona) return;
    setOcupado(true);
    setRechazo(null);
    try {
      const error = await cambios.eliminar(String(persona.id));
      if (error) {
        setRechazo(
          error.code === 'staff.tiene_historial'
            ? { tipo: 'historial', ordenes: Number(error.orderCount ?? 0), activa: error.isActive ?? true }
            : { tipo: 'otro', texto: error.code ? traducirError(error.code, t) : sinRespuesta },
        );
        return;
      }
    } catch {
      // Sin red la petición lanza en vez de responder.
      setRechazo({ tipo: 'otro', texto: sinRespuesta });
      return;
    } finally {
      setOcupado(false);
    }
    avisar({ titulo: t('admin.personal.aviso.eliminado', { nombre }) });
    onCerrar();
    onEliminado?.();
  }

  async function desactivar() {
    if (!persona) return;
    setOcupado(true);
    setFallo(null);
    try {
      const error = await cambios.cambiarEstado(String(persona.id), false);
      if (error) {
        setFallo(error.code ? traducirError(error.code, t) : sinRespuesta);
        return;
      }
    } catch {
      setFallo(sinRespuesta);
      return;
    } finally {
      setOcupado(false);
    }
    avisar({ titulo: t('admin.personal.aviso.desactivado', { nombre }) });
    // Desactivada sigue existiendo: quien abrió la ventana se queda donde estaba.
    setRechazo(null);
    onCerrar();
  }

  if (rechazo?.tipo === 'historial' && persona) {
    // Cero órdenes es posible: firmó cambios sin aparecer en ninguna orden.
    const historial =
      rechazo.ordenes > 0
        ? t('admin.personal.eliminar.bloqueado.ordenes', { n: rechazo.ordenes })
        : t('admin.personal.eliminar.bloqueado.cambios');
    return (
      <Ventana
        key="rechazo"
        abierta
        titulo={t('admin.personal.eliminar.bloqueado.titulo')}
        descripcion={t(
          rechazo.activa ? 'admin.personal.eliminar.bloqueado.activo' : 'admin.personal.eliminar.bloqueado.inactivo',
          { nombre, historial },
        )}
        acciones={
          rechazo.activa ? (
            <>
              <Boton variante="texto" disabled={ocupado} onClick={cerrar}>
                {t('admin.personal.eliminar.entendido')}
              </Boton>
              <Boton disabled={ocupado} aria-busy={ocupado} onClick={() => void desactivar()}>
                {t('admin.personal.eliminar.desactivar')}
              </Boton>
            </>
          ) : (
            <Boton onClick={cerrar}>{t('admin.personal.eliminar.entendido')}</Boton>
          )
        }
      >
        {fallo && <AvisoError aviso={{ titulo: t('admin.personal.error.titulo'), texto: fallo }} />}
      </Ventana>
    );
  }

  return (
    <Ventana
      key="confirmar"
      abierta={persona !== null}
      titulo={t('admin.personal.eliminar.titulo')}
      descripcion={persona && t('admin.personal.eliminar.texto', { nombre, codigo: persona.employeeCode })}
      acciones={
        <>
          <Boton variante="texto" disabled={ocupado} onClick={cerrar}>
            {t('app.cancelar')}
          </Boton>
          <Boton variante="peligro" disabled={ocupado} aria-busy={ocupado} onClick={() => void eliminar()}>
            {t('admin.personal.eliminar.boton')}
          </Boton>
        </>
      }
    >
      {rechazo?.tipo === 'otro' && (
        <AvisoError aviso={{ titulo: t('admin.personal.error.titulo'), texto: rechazo.texto }} />
      )}
    </Ventana>
  );
}
